import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, Phone, Pill, FileText, User, Heart, Home } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackProfileView, trackProfileScan } from "@/utils/analytics";
import { toast } from "sonner";
import { MetaTags } from "@/components/MetaTags";

interface Medication { name: string; notes?: string; }
interface Allergy { name: string; notes?: string; }
interface Condition { name: string; notes?: string; attackStatus?: string; }

interface EmergencyProfile {
  id: string;
  photo: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  bloodType: string;
  emergencyContacts: { name: string; phone: string; relation: string }[];
  medications: Medication[];
  allergies: Allergy[];
  conditions: Condition[];
  additionalNotes: string;
}

const ProfileView = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState<EmergencyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [expired, setExpired] = useState(false);

  const mapProfile = (data: any): EmergencyProfile => {
    const parseList = (d: any) =>
      Array.isArray(d) ? d.map((x: any) => (typeof x === "string" ? { name: x } : x)) : [];
    return {
      id: data.id,
      photo: data.photo_url,
      firstName: data.first_name,
      lastName: data.last_name,
      dateOfBirth: data.date_of_birth || "",
      bloodType: data.blood_type || "",
      emergencyContacts: (data.emergency_contacts as { name: string; phone: string; relation: string }[]) || [],
      medications: parseList(data.medications),
      allergies: parseList(data.allergies),
      conditions: parseList(data.conditions),
      additionalNotes: data.additional_notes || "",
    };
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("get-public-profile", {
          body: { profileId: id },
        });

        if (error) {
          console.error("Error fetching profile:", error);
          setLoading(false);
          return;
        }

        if (data?.expired) {
          setExpired(true);
          setLoading(false);
          return;
        }

        if (data?.passwordRequired) {
          setPasswordRequired(true);
          setLoading(false);
          return;
        }

        if (data?.profile) {
          setProfile(mapProfile(data.profile));
          trackScan(id);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  const trackScan = async (profileId: string) => {
    try {
      await supabase.functions.invoke("track-profile-scan", {
        body: { profileId, location: null },
      });
    } catch (error) {
      console.error("Error tracking profile scan:", error);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password || !id) return;

    try {
      const { data, error } = await supabase.functions.invoke("get-public-profile", {
        body: { profileId: id, password },
      });

      if (error || !data || data.invalidPassword || !data.profile) {
        setPasswordError(true);
        return;
      }

      setProfile(mapProfile(data.profile));
      setPasswordRequired(false);
      trackScan(id);
    } catch (error) {
      console.error("Password verification error:", error);
      setPasswordError(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Card className="p-8 glass text-center max-w-md">
          <p className="text-lg text-muted-foreground">Lädt Profil...</p>
        </Card>
      </div>
    );
  }

  if (passwordRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-4">
        <Card className="p-8 glass max-w-md w-full">
          <div className="text-center mb-6">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-primary" />
            <h1 className="text-2xl font-bold mb-2">Geschütztes Profil</h1>
            <p className="text-muted-foreground">
              Dieses Profil ist passwortgeschützt. Bitte geben Sie das Passwort ein.
            </p>
          </div>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Passwort eingeben"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError(false);
              }}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
            />
            {passwordError && (
              <p className="text-sm text-destructive">Falsches Passwort. Bitte versuchen Sie es erneut.</p>
            )}
            <Button onClick={handlePasswordSubmit} className="w-full">
              Entsperren
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-4">
        <Card className="p-8 glass text-center max-w-md">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
          <h1 className="text-2xl font-bold mb-2">Profil vorübergehend gesperrt</h1>
          <p className="text-muted-foreground mb-6">
            Aus Sicherheitsgründen muss dieses MediQR-Profil regelmäßig vom Inhaber bestätigt werden,
            damit die medizinischen Informationen aktuell bleiben. Die Bestätigungsfrist ist abgelaufen.
            <br /><br />
            Bitte informiere den Profil-Inhaber, dass er sich in seinem MediQR-Konto anmelden und das Profil neu bestätigen muss.
          </p>
          <Link to="/">
            <Button className="bg-gradient-primary">
              <Home className="mr-2 h-4 w-4" />
              Zur Startseite
            </Button>
          </Link>
        </Card>
      </div>
    );
  }


  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Card className="p-8 glass text-center max-w-md">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
          <h1 className="text-2xl font-bold mb-2">Profil nicht gefunden</h1>
          <p className="text-muted-foreground mb-6">
            Das angeforderte Notfallprofil konnte nicht geladen werden.
          </p>
          <Link to="/">
            <Button className="bg-gradient-primary">
              <Home className="mr-2 h-4 w-4" />
              Zur Startseite
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const calculateAge = (birthDate: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(profile.dateOfBirth);

  return (
    <>
      <MetaTags
        title="Notfallprofil - MediQR"
        description="Notfallinformationen - Nur für autorisierte Rettungskräfte"
        noIndex={true}
      />
      <div className="min-h-screen bg-gradient-subtle py-8 px-4">
        <div className="container mx-auto max-w-4xl">
        {/* Emergency Banner */}
        <div className="mb-6 p-4 bg-destructive/10 border-2 border-destructive rounded-lg flex items-center gap-3">
          <AlertCircle className="h-6 w-6 text-destructive flex-shrink-0" />
          <div>
            <p className="font-semibold text-destructive">NOTFALLINFORMATIONEN</p>
            <p className="text-sm text-muted-foreground">
              Diese Informationen sind für medizinisches Notfallpersonal bestimmt
            </p>
          </div>
        </div>

        {/* Main Profile Card */}
        <Card className="p-8 glass mb-6">
          <div className="flex flex-col md:flex-row gap-6 mb-8">
            <img
              src={profile.photo}
              alt={`${profile.firstName} ${profile.lastName}`}
              className="w-32 h-32 rounded-full object-cover border-4 border-primary shadow-glow mx-auto md:mx-0"
            />
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl font-bold mb-2">
                {profile.firstName} {profile.lastName}
              </h1>
              <div className="space-y-1 text-muted-foreground">
                {age && <p className="text-lg">Alter: {age} Jahre</p>}
                {profile.dateOfBirth && (
                  <p>
                    Geburtsdatum:{" "}
                    {new Date(profile.dateOfBirth).toLocaleDateString("de-DE", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                )}
                {profile.bloodType && (
                  <p className="text-xl font-semibold text-primary">
                    Blutgruppe: {profile.bloodType}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Emergency Contacts */}
          {profile.emergencyContacts.some((c) => c.name) && (
            <div className="mb-8 p-6 bg-primary/5 rounded-xl border-2 border-primary/20">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Phone className="h-6 w-6 text-primary" />
                Notfallkontakte
              </h2>
              <div className="space-y-3">
                {profile.emergencyContacts
                  .filter((c) => c.name)
                  .map((contact, i) => (
                    <div
                      key={i}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-background rounded-lg"
                    >
                      <div>
                        <p className="font-semibold text-lg">{contact.name}</p>
                        <p className="text-sm text-muted-foreground">{contact.relation}</p>
                      </div>
                      <a
                        href={`tel:${contact.phone}`}
                        className="text-primary font-semibold text-lg hover:underline mt-2 sm:mt-0"
                      >
                        {contact.phone}
                      </a>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* Medications */}
            {profile.medications.some((m) => m.name) && (
              <div className="p-6 bg-accent/5 rounded-xl border border-accent/20">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Pill className="h-5 w-5 text-accent" />
                  Medikamente
                </h2>
                <ul className="space-y-3">
                  {profile.medications
                    .filter((m) => m.name)
                    .map((med, i) => (
                      <li key={i} className="space-y-1">
                        <div className="flex items-start gap-2">
                          <span className="text-accent mt-1">•</span>
                          <span className="font-medium">{med.name}</span>
                        </div>
                        {med.notes && (
                          <p className="text-sm text-muted-foreground ml-5">{med.notes}</p>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Allergies */}
            {profile.allergies.some((a) => a.name) && (
              <div className="p-6 bg-destructive/5 rounded-xl border-2 border-destructive/30">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  ⚠️ Allergien
                </h2>
                <ul className="space-y-3">
                  {profile.allergies
                    .filter((a) => a.name)
                    .map((allergy, i) => (
                      <li key={i} className="space-y-1">
                        <div className="flex items-start gap-2">
                          <span className="text-destructive mt-1">•</span>
                          <span className="font-medium">{allergy.name}</span>
                        </div>
                        {allergy.notes && (
                          <p className="text-sm text-muted-foreground ml-5">{allergy.notes}</p>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Medical Conditions */}
            {profile.conditions.some((c) => c.name) && (
              <div className="p-6 bg-primary/5 rounded-xl border border-primary/20">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Vorerkrankungen
                </h2>
                <ul className="space-y-3">
                  {profile.conditions
                    .filter((c) => c.name)
                    .map((condition, i) => (
                      <li key={i} className="space-y-1">
                        <div className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <div>
                            <span className="font-medium">{condition.name}</span>
                            {condition.attackStatus && (
                              <span className="ml-2 text-xs bg-primary/10 px-2 py-0.5 rounded">
                                {condition.attackStatus === 'akut' && 'Akuter Schub'}
                                {condition.attackStatus === 'remission' && 'In Remission'}
                                {condition.attackStatus === 'stabil' && 'Chronisch stabil'}
                              </span>
                            )}
                          </div>
                        </div>
                        {condition.notes && (
                          <p className="text-sm text-muted-foreground ml-5">{condition.notes}</p>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Additional Notes */}
            {profile.additionalNotes && (
              <div className="p-6 bg-muted/50 rounded-xl border border-border">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-foreground" />
                  Zusätzliche Notizen
                </h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {profile.additionalNotes}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Footer Info */}
        <Card className="p-6 glass text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="h-5 w-5 text-primary" />
            <span className="font-semibold gradient-text">MediQR by <a href="https://diekerit.com?referer=mediqr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DiekerIT</a></span>
          </div>
          <p className="text-sm text-muted-foreground">
            Diese Informationen wurden mit MediQR erstellt
          </p>
          <Link to="/">
            <Button variant="outline" className="mt-4">
              <Home className="mr-2 h-4 w-4" />
              Eigenen QR-Code erstellen
            </Button>
          </Link>
        </Card>
        </div>
      </div>
    </>
  );
};

export default ProfileView;
